/**
 * Set filters hook - manages filter state for Set Details page
 *
 * Supports filtering by search, rarity, colors, card type, CMC, and ownership.
 */
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useCallback, useMemo } from 'react';

import { CMC_VALUES, type CmcFilterValue } from '@/components/CmcFilter/CmcFilter';
import type { Color, Rarity } from '@/types/card';
import type { SpecialColor } from '@/types/schemas';

// ============================================================================
// Types
// ============================================================================

/** Card type categories for filtering */
export type SetCardType = 'creature' | 'instant' | 'sorcery' | 'enchantment' | 'artifact' | 'land' | 'planeswalker' | 'battle';

/** Re-export CmcFilterValue for convenience */
export type { CmcFilterValue as CmcFilter };

/** Ownership filter options */
export type OwnershipFilter = 'all' | 'owned' | 'not-owned';

/** In-deck filter options */
export type InDeckFilter = 'in-deck' | 'not-in-deck';

/** In-list filter options */
export type InListFilter = 'in-list' | 'not-in-list';

/** Filter state for Set Details page */
export interface SetFilters {
  /** Search query for name and type line */
  search: string;
  /** Filter by rarities */
  rarities: Rarity[];
  /** Filter by color identity (any match) */
  colors: Color[];
  /** Filter by special colors (multicolor/colorless) */
  specialColors: SpecialColor[];
  /** Filter by card types */
  cardTypes: SetCardType[];
  /** Filter by converted mana costs */
  cmcValues: CmcFilterValue[];
  /** Filter by ownership status */
  ownership: OwnershipFilter;
  /** Filter by deck membership */
  inDeckFilter: InDeckFilter | undefined;
  /** Specific deck ID for deck membership filter */
  deckIdFilter: string | undefined;
  /** Filter by list membership */
  inListFilter: InListFilter | undefined;
  /** Specific list ID for list membership filter */
  listIdFilter: string | undefined;
}

// ============================================================================
// Constants
// ============================================================================

export const SET_CARD_TYPES: { value: SetCardType; label: string }[] = [
  { value: 'creature', label: 'Creature' },
  { value: 'instant', label: 'Instant' },
  { value: 'sorcery', label: 'Sorcery' },
  { value: 'enchantment', label: 'Enchantment' },
  { value: 'artifact', label: 'Artifact' },
  { value: 'land', label: 'Land' },
  { value: 'planeswalker', label: 'Planeswalker' },
  { value: 'battle', label: 'Battle' },
];

/** Re-export CMC_VALUES for consumers */
export { CMC_VALUES };

export const OWNERSHIP_OPTIONS: { value: OwnershipFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'owned', label: 'Owned' },
  { value: 'not-owned', label: 'Not Owned' },
];

export const FILTER_RARITIES: Rarity[] = ['common', 'uncommon', 'rare', 'mythic'];

// ============================================================================
// State Atoms
// ============================================================================

const defaultFilters: SetFilters = {
  search: '',
  rarities: [],
  colors: [],
  specialColors: [],
  cardTypes: [],
  cmcValues: [],
  ownership: 'all',
  inDeckFilter: undefined,
  deckIdFilter: undefined,
  inListFilter: undefined,
  listIdFilter: undefined,
};

/** Set filters state atom (persisted to localStorage) */
export const setFiltersAtom = atomWithStorage<SetFilters>('mtgcraft:set-filters', defaultFilters);

// ============================================================================
// Hook
// ============================================================================

export interface UseSetFiltersReturn {
  /** Current filter state */
  filters: SetFilters;
  /** Set the search query */
  setSearch: (search: string) => void;
  /** Toggle a rarity filter */
  toggleRarity: (rarity: Rarity) => void;
  /** Toggle a color filter */
  toggleColor: (color: Color) => void;
  /** Toggle a special color filter (multicolor/colorless) */
  toggleSpecialColor: (special: SpecialColor) => void;
  /** Toggle a card type filter */
  toggleCardType: (cardType: SetCardType) => void;
  /** Toggle a CMC filter */
  toggleCmc: (cmc: CmcFilterValue) => void;
  /** Set the ownership filter */
  setOwnership: (ownership: OwnershipFilter) => void;
  /** Set the in-deck filter */
  setInDeckFilter: (value: InDeckFilter | undefined) => void;
  /** Set the deck ID filter */
  setDeckIdFilter: (deckId: string | undefined) => void;
  /** Set the in-list filter */
  setInListFilter: (value: InListFilter | undefined) => void;
  /** Set the list ID filter */
  setListIdFilter: (listId: string | undefined) => void;
  /** Clear all filters */
  clearFilters: () => void;
  /** Whether any filters are active */
  hasActiveFilters: boolean;
  /** Count of active filters (excluding search) */
  activeFilterCount: number;
}

/**
 * Hook to manage set card filters
 */
export default function useSetFilters(): UseSetFiltersReturn {
  const [filters, setFilters] = useAtom(setFiltersAtom);

  const setSearch = useCallback(
    (search: string) => {
      setFilters((prev) => ({ ...prev, search }));
    },
    [setFilters],
  );

  const toggleRarity = useCallback(
    (rarity: Rarity) => {
      setFilters((prev) => ({
        ...prev,
        rarities: prev.rarities.includes(rarity) ? prev.rarities.filter((r) => r !== rarity) : [...prev.rarities, rarity],
      }));
    },
    [setFilters],
  );

  const toggleColor = useCallback(
    (color: Color) => {
      setFilters((prev) => ({
        ...prev,
        colors: prev.colors.includes(color) ? prev.colors.filter((c) => c !== color) : [...prev.colors, color],
      }));
    },
    [setFilters],
  );

  const toggleSpecialColor = useCallback(
    (special: SpecialColor) => {
      setFilters((prev) => ({
        ...prev,
        specialColors: prev.specialColors.includes(special)
          ? prev.specialColors.filter((s) => s !== special)
          : [...prev.specialColors, special],
      }));
    },
    [setFilters],
  );

  const toggleCardType = useCallback(
    (cardType: SetCardType) => {
      setFilters((prev) => ({
        ...prev,
        cardTypes: prev.cardTypes.includes(cardType) ? prev.cardTypes.filter((t) => t !== cardType) : [...prev.cardTypes, cardType],
      }));
    },
    [setFilters],
  );

  const toggleCmc = useCallback(
    (cmc: CmcFilterValue) => {
      setFilters((prev) => ({
        ...prev,
        cmcValues: prev.cmcValues.includes(cmc) ? prev.cmcValues.filter((c) => c !== cmc) : [...prev.cmcValues, cmc],
      }));
    },
    [setFilters],
  );

  const setOwnership = useCallback(
    (ownership: OwnershipFilter) => {
      setFilters((prev) => ({ ...prev, ownership }));
    },
    [setFilters],
  );

  const setInDeckFilter = useCallback(
    (value: InDeckFilter | undefined) => {
      setFilters((prev) => ({
        ...prev,
        inDeckFilter: value,
        // Clear deck ID when filter is cleared
        deckIdFilter: value ? prev.deckIdFilter : undefined,
      }));
    },
    [setFilters],
  );

  const setDeckIdFilter = useCallback(
    (deckId: string | undefined) => {
      setFilters((prev) => ({ ...prev, deckIdFilter: deckId }));
    },
    [setFilters],
  );

  const setInListFilter = useCallback(
    (value: InListFilter | undefined) => {
      setFilters((prev) => ({
        ...prev,
        inListFilter: value,
        // Clear list ID when filter is cleared
        listIdFilter: value ? prev.listIdFilter : undefined,
      }));
    },
    [setFilters],
  );

  const setListIdFilter = useCallback(
    (listId: string | undefined) => {
      setFilters((prev) => ({ ...prev, listIdFilter: listId }));
    },
    [setFilters],
  );

  const clearFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, [setFilters]);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.search.length > 0 ||
      filters.rarities.length > 0 ||
      filters.colors.length > 0 ||
      filters.specialColors.length > 0 ||
      filters.cardTypes.length > 0 ||
      filters.cmcValues.length > 0 ||
      filters.ownership !== 'all' ||
      filters.inDeckFilter !== undefined ||
      filters.inListFilter !== undefined
    );
  }, [filters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.rarities.length > 0) count++;
    if (filters.colors.length > 0) count++;
    if (filters.specialColors.length > 0) count++;
    if (filters.cardTypes.length > 0) count++;
    if (filters.cmcValues.length > 0) count++;
    if (filters.ownership !== 'all') count++;
    if (filters.inDeckFilter) count++;
    if (filters.inListFilter) count++;
    return count;
  }, [filters]);

  return {
    filters,
    setSearch,
    toggleRarity,
    toggleColor,
    toggleSpecialColor,
    toggleCardType,
    toggleCmc,
    setOwnership,
    setInDeckFilter,
    setDeckIdFilter,
    setInListFilter,
    setListIdFilter,
    clearFilters,
    hasActiveFilters,
    activeFilterCount,
  };
}
